import { useMemo } from 'react';
import {
  useQuery,
  keepPreviousData,
  type QueryClient,
} from '@tanstack/react-query';
import { getInvestigationReport } from '@apis/investigationReports';
import {
  fetchProfessionAllTimeStats,
  fetchProfessionAnalysis,
  fetchProfessionLocations,
  fetchProjectFloors,
} from '@apis/contractorAnalysis';
import { fetchSiteProfessions } from '@apis/professions';
import { sortFloorsAscending } from '../sortFloors';
import {
  EMPTY_ALL_TIME,
  EMPTY_ANALYSIS,
  EMPTY_LOCATIONS,
  averageOver,
  buildLocationViews,
  formatWorkingSince,
  projectAnalysisDay,
} from '../helpers';

interface Args {
  siteId: string;
  reportId: string;
  dateFrom: string;
  dateTo: string;
  tz: string;
}

/**
 * Single hook that owns the five TanStack queries the Contractor Analysis
 * report depends on (record, all-time stats, per-day analysis, location
 * roll-ups, project floor catalog, working profession catalog) AND the
 * memoized projections used by the chart + location distribution panel.
 *
 * Each derived view (`series`, `floors`, `locationRows`, ...) is `useMemo`-d
 * on its source query data, so a render triggered by an unrelated query
 * settling doesn't re-run the sort / map / reduce work.
 *
 * `analysisQuery` and `locationsQuery` use `placeholderData: keepPreviousData`
 * so the chart + location panel don't blank out between date-range changes
 * while the new fetch resolves.
 */
export function useContractorAnalysisData({ siteId, reportId, dateFrom, dateTo, tz }: Args) {
  const recordQuery = useQuery({
    queryKey: ['investigationReport', siteId, reportId] as const,
    queryFn: ({ signal }) => getInvestigationReport(siteId, reportId, signal),
    enabled: !!siteId && !!reportId,
  });

  const record = recordQuery.data ?? null;
  const isContractorAnalysis = record?.investigationType === 'contractorAnalysis';
  const professionId = record?.professionId ?? '';

  // All-time stats — feeds the hero strip (Working Since / Total Hours /
  // Avg workers / Avg hours). Cumulative since the contractor's first day.
  const allTimeQuery = useQuery({
    queryKey: ['contractor-all-time-stats', siteId, professionId] as const,
    queryFn: ({ signal }) => fetchProfessionAllTimeStats(siteId, professionId, signal),
    enabled: !!siteId && !!professionId,
    staleTime: 5 * 60_000,
  });

  // Per-day workers + hours analysis for the selected range.
  const analysisQuery = useQuery({
    queryKey: ['contractor-analysis', siteId, professionId, dateFrom, dateTo] as const,
    queryFn: ({ signal }) =>
      fetchProfessionAnalysis(siteId, professionId, dateFrom, dateTo, signal),
    enabled: !!siteId && !!professionId && !!dateFrom && !!dateTo,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  // Per-floor / per-area aggregation for the selected range.
  const locationsQuery = useQuery({
    queryKey: ['contractor-locations', siteId, professionId, dateFrom, dateTo] as const,
    queryFn: ({ signal }) =>
      fetchProfessionLocations(siteId, professionId, dateFrom, dateTo, signal),
    enabled: !!siteId && !!professionId && !!dateFrom && !!dateTo,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  // Project floor catalog — overrides snapshotted floorNick on productivity
  // records (which may be stale in other languages) and drives canonical
  // floor order. Cached for 1h: floors are rarely edited and the catalog is small.
  const floorsQuery = useQuery({
    queryKey: ['site-floors', siteId] as const,
    queryFn: ({ signal }) => fetchProjectFloors(siteId, signal),
    enabled: !!siteId,
    staleTime: 60 * 60_000,
  });

  // Working professions catalog — used by the hero strip's Trade chip.
  const professionsQuery = useQuery({
    queryKey: ['site-professions', siteId] as const,
    queryFn: ({ signal }) => fetchSiteProfessions(siteId, signal),
    enabled: !!siteId,
    staleTime: 5 * 60_000,
  });

  // BFF shapes projected into the sub-component shapes. Each is memoized
  // on its specific source data so unrelated query state changes don't
  // re-trigger the sort / map / reduce work.
  const allTime = allTimeQuery.data ?? EMPTY_ALL_TIME;
  const analysis = analysisQuery.data ?? EMPTY_ANALYSIS;
  const locations = locationsQuery.data ?? EMPTY_LOCATIONS;

  const series = useMemo(() => analysis.days.map(projectAnalysisDay), [analysis]);
  const rangeAvgWorkers = useMemo(() => averageOver(series, (d) => d.workers), [series]);
  const rangeAvgHours = useMemo(() => averageOver(series, (d) => d.hours), [series]);

  // Total working hours displayed in the Location Distribution header comes
  // from the analysis endpoint (distinct work-day hours), not from summing
  // per-floor productivity time — those productivity sums multi-count
  // overlapping presence and run ~2-3× higher. Matches trusstor's
  // `getTotalTimeFromProfessionAnalysisReport` semantics.
  const totalLocationsHours = useMemo(
    () => analysis.days.reduce((acc, day) => acc + day.totalWorkHours, 0),
    [analysis],
  );

  // Pre-sort the catalog so the order map indexes match trusstor's visual
  // order — basements before above-grade, numeric not lexicographic.
  const floors = useMemo(() => sortFloorsAscending(floorsQuery.data ?? []), [floorsQuery.data]);

  const { rows: locationRows, activeLocations } = useMemo(
    () => buildLocationViews(locations, floors),
    [locations, floors],
  );

  const matchedProfession = useMemo(
    () => professionsQuery.data?.find((profession) => profession.id === professionId),
    [professionsQuery.data, professionId],
  );

  const workingSinceLabel = useMemo(
    () => formatWorkingSince(allTime.firstWorkingDayIso, tz),
    [allTime.firstWorkingDayIso, tz],
  );

  const hasSecondaryError =
    allTimeQuery.isError ||
    analysisQuery.isError ||
    locationsQuery.isError ||
    floorsQuery.isError;

  return {
    // Branching flags + the record itself for the loading / not-found guards:
    recordIsLoading: recordQuery.isLoading,
    record,
    isContractorAnalysis,
    professionId,
    // Hero strip:
    allTime,
    workingSinceLabel,
    matchedProfession,
    // Workers-per-day chart:
    series,
    rangeAvgWorkers,
    rangeAvgHours,
    // Location distribution panel:
    locationRows,
    activeLocations,
    totalLocationsHours,
    // Secondary-error banner trigger:
    hasSecondaryError,
  };
}

/**
 * Invalidate the four secondary queries (all-time stats, per-day analysis,
 * location aggregation, floor catalog) the Contractor Analysis report depends
 * on. Each key is scoped to `siteId` so retrying on this page can't wipe
 * cached entries for other sites in the same React tree. Co-located with the
 * hook so query keys stay in lockstep with the queries that produce them.
 */
export function invalidateContractorAnalysisSecondary(
  queryClient: QueryClient,
  siteId: string,
  professionId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: ['contractor-all-time-stats', siteId, professionId],
  });
  void queryClient.invalidateQueries({
    queryKey: ['contractor-analysis', siteId, professionId],
  });
  void queryClient.invalidateQueries({
    queryKey: ['contractor-locations', siteId, professionId],
  });
  void queryClient.invalidateQueries({ queryKey: ['site-floors', siteId] });
}
